/**
 * Entry point for the Mahjong Quiz Vue application.
 *
 * Responsibilities:
 *  - Bootstraps the Vue app
 *  - Provides a simple global injectable for API base resolution
 *  - (Future) Could register global components / router / state management
 *
 * API Access:
 *  By default the frontend calls same-origin `/api/...`.
 *  You can override the base at build time with `VITE_API_BASE`.
 *
 *  Example:
 *    VITE_API_BASE=https://example.com cargo run  (when served statically)
 *
 *  In dev, Vite's proxy (see vite.config.ts) forwards `/api` to the Axum server.
 */

import { createApp, inject } from 'vue';
import App from './App.vue';

// Symbol for optional global injection (in case we expand later).
export const ApiConfigSymbol = Symbol('ApiConfig');

export interface ApiConfig {
  baseUrl: string;
  /**
   * Helper to build an absolute URL for an API path (leading slash optional).
   */
  url(path: string): string;
}

/**
 * Resolve API base:
 *  1. VITE_API_BASE if defined
 *  2. Otherwise use same-origin (empty string)
 */
function resolveApiConfig(): ApiConfig {
  const base = import.meta.env.VITE_API_BASE?.replace(/\/+$/, '') || '';
  return {
    baseUrl: base,
    url: (p: string) => {
      const clean = p.startsWith('/') ? p : `/${p}`;
      return `${base}${clean}`;
    },
  };
}

// (Optional) Convenience composable for later modules.
export function useApiConfig(): ApiConfig {
  const cfg = inject<ApiConfig>(ApiConfigSymbol);
  if (!cfg) {
    throw new Error('ApiConfig not provided (did you mount the app correctly?)');
  }
  return cfg;
}

const app = createApp(App);

// Provide the API config globally (access via useApiConfig()).
app.provide(ApiConfigSymbol, resolveApiConfig());

app.mount('#app');

// (Optional) Hot Module Replacement accept block (not strictly needed, but explicit)
if (import.meta.hot) {
  import.meta.hot.accept();
}
